import { Account } from '@/domain/usecases'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import CompanyManagerBaseLoader from '@/main/routes/loaders/company-manager-base-loader'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import { LoaderFunctionArgs } from 'react-router-dom'

export default class CompanyManagerDependentRegistrationLoader extends CompanyManagerBaseLoader {
  private static readonly ACCOUNT_ID_PATH_PARAM_NAME = 'accountId'

  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined
  ) {
    super(account, undefined, queryClient, ldClient, FeatureFlag.Key.COMPANIES_ON_DEPENDENT_ROLLOUT)
  }

  async loader({ params }: LoaderFunctionArgs) {
    const accountId = params[CompanyManagerDependentRegistrationLoader.ACCOUNT_ID_PATH_PARAM_NAME]

    if (!accountId) return null

    return this.defer({
      data: Promise.all([
        this.promiseAccountProfile(accountId),
        this.promiseDependentRegistrationOptions(accountId),
        super.decodeImportImage(import('@images/completed.svg'))
      ]).then(([accountProfile, dependentRegistrationOptions, successImage]) => ({
        holderAccountProfile: accountProfile,
        dependentRegistrationOptions,
        successImage
      }))
    })
  }

  private async promiseAccountProfile(accountId: string) {
    return import('@remote/account/company-manager/remote-company-manager-account-profile-factory')
      .then(module => module.makeRemoteCompanyManagerAccountProfile)
      .then(async makeRemoteCompanyManagerAccountProfile => {
        const remote = makeRemoteCompanyManagerAccountProfile(accountId, this.queryClient)
        return remote.profile()
      })
  }

  private async promiseDependentRegistrationOptions(accountId: string) {
    return import(
      '@remote/account/company-manager/remote-company-manager-dependent-registration-options-factory'
    )
      .then(module => module.makeRemoteCompanyManagerDependentRegistrationOptions)
      .then(async makeRemoteCompanyManagerAccountProfile => {
        const remote = makeRemoteCompanyManagerAccountProfile(accountId, this.queryClient)
        return remote.loader()
      })
  }
}
